/**
 * @fileoverview A faixa "confirme seu e-mail" (30/10/2026, etapa 4 dos planos):
 * para quem está logado sem o e-mail confirmado (o cadastro pelo próprio
 * usuário e o convite público do Chat Interno). Sem confirmar, as operações
 * sensíveis são recusadas (convidar, contratar, links públicos, chaves da API);
 * a faixa diz isso e reenvia o link. Fechada, volta na próxima vez que a tela
 * abrir. Confirmado em outra aba, ela some (a tela da confirmação avisa pelo
 * armazenamento do navegador). No "Acessar como" não aparece: o super não
 * passa pela trava, e o reenvio iria para o e-mail do cliente.
 *
 * Sem permissão do catálogo, de propósito: é um aviso sobre a própria conta de
 * quem está logado, e o reenvio usa a rota pública (a mesma resposta para
 * qualquer e-mail).
 */

import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { MailCheck, X } from "lucide-react";

import { AuthContext } from "../../context/Auth/AuthContext";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { EMAIL_CONFIRMED_KEY } from "../AuthLayout/authHelpers";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    bar: {
      flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "8px 10px 8px 20px", fontSize: 13, fontWeight: 500, lineHeight: 1.45,
      background: dark ? "#221a0a" : "#FFFBEB", color: dark ? "#FDE68A" : "#78350F",
      borderBottom: `1px solid ${dark ? "rgba(251,191,36,0.16)" : "#FDE68A"}`,
      "& > *": { animation: "$in 180ms ease-out" },
      "@media (prefers-reduced-motion: reduce)": { "& > *": { animation: "none" } },
      [theme.breakpoints.down("sm")]: { padding: "8px 6px 8px 16px", fontSize: 12.5, flexWrap: "wrap" },
    },
    text: { flex: 1, minWidth: 0 },
    action: {
      flexShrink: 0, border: `1px solid ${dark ? "rgba(253,230,138,0.35)" : "rgba(120,53,15,0.3)"}`, background: "transparent", color: "inherit",
      borderRadius: 8, padding: "5px 10px", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
      "&:hover:not(:disabled)": { background: dark ? "rgba(255,255,255,0.06)" : "rgba(120,53,15,0.06)" },
      "&:disabled": { opacity: 0.7, cursor: "default" },
      "&:focus-visible": { outline: "1px solid currentColor" },
    },
    close: {
      width: 30, height: 30, flexShrink: 0, border: "none", borderRadius: 8, background: "transparent", color: "inherit", display: "inline-flex",
      alignItems: "center", justifyContent: "center", cursor: "pointer", opacity: 0.7,
      "&:hover": { opacity: 1, background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
      "&:focus-visible": { outline: "1px solid currentColor", opacity: 1 },
    },
    "@keyframes in": { from: { opacity: 0, transform: "translateY(-4px)" }, to: { opacity: 1, transform: "none" } },
  };
});

const EmailVerificationBanner = () => {
  const classes = useStyles();
  const { user, impersonation } = useContext(AuthContext);
  const [closed, setClosed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const confirmedElsewhere = (event) => event.key === EMAIL_CONFIRMED_KEY && setClosed(true);
    window.addEventListener("storage", confirmedElsewhere);
    return () => window.removeEventListener("storage", confirmedElsewhere);
  }, []);

  if (closed || impersonation || user?.emailVerified !== false || !user?.email) return null;

  const resend = async () => {
    setSending(true);
    try {
      await openApi.post("/auth/verify-email/resend", { email: user.email });
      setSent(true);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={classes.bar} role="status">
      <MailCheck size={16} aria-hidden="true" />
      <span className={classes.text}>{sent ? i18n.t("auth.banner.sent", { email: user.email }) : i18n.t("auth.banner.text", { email: user.email })}</span>
      {!sent && (
        <button type="button" className={classes.action} onClick={resend} disabled={sending}>{i18n.t("auth.resend")}</button>
      )}
      <button type="button" className={classes.close} onClick={() => setClosed(true)} aria-label={i18n.t("auth.banner.close")} title={i18n.t("auth.banner.close")}>
        <X size={15} />
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
